import { CalculateConfigurationMasterGroupParticipantSourceDataAllocations, ConfigurationMasterGroup as ConfigurationMasterGroupDto, ConfigurationStatus, SaveConfigurationMasterGroup, SourceDataAllocationType } from "@/api/dtos";
import InputOnlyField from "@/components/forms/fields/inputOnly/InputOnlyField";
import Text from "@/components/forms/inputs/Text";
import SetLinks from "@/components/layouts/SetLinks";
import useConfigurationMasterGroup from "@/hooks/data/useConfigurationMasterGroup";
import { FC, useCallback, useContext, useMemo } from "react";

import UneditableText from "@/components/forms/inputs/UneditableText";
import ConfigurationContextTemplate, { IConfigurationContextTemplateProps } from "@/components/forms/templates/ConfigurationContextTemplate";
import MasterGroupContext from "@/contexts/data/MasterGroupContext";
import TrialContext from "@/contexts/data/TrialContext";
import { ConfigurationMasterGroupsTrialCreateLink } from "@/contexts/layouts/LinkContext";
import { useLocation } from "react-router-dom";


const allocationTypes = [
    SourceDataAllocationType.Constant,
    SourceDataAllocationType.FirstXParticipants,
    SourceDataAllocationType.RepeatingCycle,
    SourceDataAllocationType.FirstXParticipantsAndRepeatingCycle,
    SourceDataAllocationType.Inherit
];

const statuses = [
    ConfigurationStatus.Disabled,
    ConfigurationStatus.ReadOnly,
    ConfigurationStatus.Enabled
];


const ConfigurationMasterGroup: FC = () => {
    const { data: trial, oid: trialOid } = useContext(TrialContext);
    const { data: masterGroup } = useContext(MasterGroupContext);

    const contextQuery = useMemo(() => {
        if (!masterGroup?.id) {
            return undefined;
        }

        return {
            trialId: masterGroup.trialId,
            masterGroupId: masterGroup.id
        }
    }, [masterGroup]);

    const { data: configurationMasterGroup, loading: configurationMasterGroupLoading, validating: configurationMasterGroupValidating } = useConfigurationMasterGroup(contextQuery, { revalidateOnFocus: false });

    const loading = configurationMasterGroupLoading;
    const validating = configurationMasterGroupValidating;

    const location = useLocation();

    const returnUrl = useMemo(() => {
        if (location.state?.from) {
            return `${location.state.from.pathname}${location.state.from.search}`
        }
        
        return `/${trialOid}/configuration/master-groups`;
    }, [trialOid, location])

    const onSave = useCallback<IConfigurationContextTemplateProps<ConfigurationMasterGroupDto>['onSave']>(async (client, data, allocations, updates) => {
        await client.post(new SaveConfigurationMasterGroup({ data: data, allocations: allocations, participantSourceDataAllocationUpdates: updates}));
    }, []);

    const onCalculateAllocations = useCallback<IConfigurationContextTemplateProps<ConfigurationMasterGroupDto>['onCalculateAllocations']>(async (client, contextQuery, data, allocations) => {
        const response = await client.post(new CalculateConfigurationMasterGroupParticipantSourceDataAllocations({ 
            ...contextQuery,
            configurationMasterGroup: data, 
            allocations: allocations
        }));

        return response;
    }, []);
    
    const onValidate = useCallback<IConfigurationContextTemplateProps<ConfigurationMasterGroupDto>['onValidate']>(async (client, errors, formState) => {

        return errors;
    }, []);

    return (
        <>
            <SetLinks 
                createTrialLink={ConfigurationMasterGroupsTrialCreateLink} 
                institutionLinkEnabled={false} 
                participantLinkEnabled={false} 
            />
            <ConfigurationContextTemplate
                allocationTypes={allocationTypes}
                entityName="Master Group"
                title={masterGroup?.name}
                onCalculateAllocations={onCalculateAllocations}
                onSave={onSave}
                onValidate={onValidate}
                statuses={statuses}
                configuration={configurationMasterGroup}
                configurationLoading={loading}
                configurationValidating={validating}
                contextQuery={contextQuery}
                returnUrl={returnUrl}
                children={
                    <>
                        <InputOnlyField
                            propertyPath="originalName"
                            label="Default Name"
                            helperText="The Default Name of the master group."
                            margin="normal"
                            component={UneditableText}
                            disabled
                            value={masterGroup?.defaultName}
                        />
                        <InputOnlyField
                            propertyPath="configuration.name"
                            label="Name"
                            helperText="Enter a name to override the Default Name of the master group."
                            margin="normal"
                            component={Text}
                        />
                    </>
                }
            />
        </>
    )
};

export default ConfigurationMasterGroup;